import { Component, computed, ElementRef, input, model, ModelSignal, output, signal, viewChild } from '@angular/core';
import { FormValueControl } from '@angular/forms/signals';

export type TimeValue = { hour: number; minute: number; second: number };

/**
 * Native time-only picker that works with a structured time value object instead of a `Date`.
 *
 * The value type is `{ hour: number; minute: number; second: number }`.
 * This avoids the 1970 anchor-date problem that arises when using `Date` for time-only concepts.
 */
@Component({
    selector: 'baas-native-time-picker',
    templateUrl: './baas-native-time-picker.html',
    host: { class: 'baas-native-time-picker baas-native-temporal-picker' }
})
export class BaasNativeTimePicker implements FormValueControl<TimeValue | null | undefined> {
    readonly input = viewChild.required<ElementRef<HTMLInputElement>>('input');
    // NOTE typing needed for d.ts generation, otherwise it becomes of type TimeValue
    readonly value: ModelSignal<TimeValue | null | undefined> = model<TimeValue | null | undefined>(undefined);
    /** Id used to associate with label for. */
    readonly labelForId = input<string>();
    /** Show seconds field. */
    readonly includeSeconds = input(false);
    /** Min selectable time as `HH:mm` or `HH:mm:ss` string. */
    readonly minTime = input<string | undefined>();
    /** Max selectable time as `HH:mm` or `HH:mm:ss` string. */
    readonly maxTime = input<string | undefined>();
    readonly readonly = input(false);
    readonly required = input<boolean>();
    readonly disabled = input<boolean>();
    /** Occurs on input blur. */
    // eslint-disable-next-line @angular-eslint/no-output-native
    readonly blur = output();
    /** Occurs when the value changes. */
    readonly changed = output<TimeValue | null>();

    protected readonly step = computed(() => (this.includeSeconds() ? 1 : 60));

    protected readonly draftValue = signal<string | null>(null);

    protected readonly inputValue = computed(() => this.draftValue() ?? this.#format(this.value()));

    focus(): void {
        this.input()?.nativeElement?.focus();
    }

    protected onInput(event: Event): void {
        const raw = (event.target as HTMLInputElement).value;
        this.draftValue.set(raw);
        if (raw) {
            const parsed = this.#parse(raw);
            if (parsed) this.value.set(parsed);
        }
    }

    protected onFocus(event: FocusEvent): void {
        this.draftValue.set((event.target as HTMLInputElement).value);
    }

    protected onInputChange(event: Event): void {
        const raw = (event.target as HTMLInputElement).value;
        this.draftValue.set(raw);
        const parsed = raw ? this.#parse(raw) : null;
        this.value.set(parsed);
        this.changed.emit(parsed);
    }

    protected onBlur(): void {
        this.draftValue.set(null);
        this.blur.emit();
    }

    #format(value: TimeValue | null | undefined): string {
        if (!value) return '';
        const pad = (n: number) => n.toString().padStart(2, '0');
        return this.includeSeconds() ? `${pad(value.hour)}:${pad(value.minute)}:${pad(value.second)}` : `${pad(value.hour)}:${pad(value.minute)}`;
    }

    #parse(raw: string): TimeValue | null {
        if (!raw) return null;
        const parts = raw.split(':').map(Number);
        const [hour, minute = 0, second = 0] = parts;
        if (Number.isNaN(hour)) return null;
        return { hour, minute, second: this.includeSeconds() ? second : 0 };
    }
}
